import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { SignOut, User, List, Stack, Gauge } from "@phosphor-icons/react";
import { useState } from "react";

const navItems = [
  { to: "/", label: "Home", end: true },
  { to: "/how-to", label: "How To" },
  { to: "/convert", label: "Converter" },
  { to: "/repair", label: "Repair" },
  { to: "/split", label: "Split" },
  { to: "/contact", label: "Contact Us" },
  { to: "/pricing", label: "Pricing" },
];

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  // Hide the brand logo on the admin portal — the wide tab strip squeezes it.
  const isAdminPortal = location.pathname.startsWith("/admin");

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header
      className="sticky top-0 z-50 bg-white border-b border-[var(--border)]"
      data-testid="app-header"
    >
      <div className="max-w-[1440px] mx-auto flex items-center justify-between gap-4 px-6 md:px-10 h-16">
        {isAdminPortal ? (
          <Link to="/" className="micro-label text-[var(--fg-2)] hover:text-[var(--accent)] shrink-0" data-testid="brand-link-admin">
            ← Back to site
          </Link>
        ) : (
          <Link
            to="/"
            className="flex items-center group shrink-0"
            data-testid="brand-link"
          >
            <img
              src="/decal-lab-logo.v558587.png"
              alt="Decal Labs"
              className="h-8 w-auto select-none shrink-0 object-contain"
              style={{ aspectRatio: "auto" }}
              draggable="false"
            />
          </Link>
        )}

        <nav className="hidden md:flex items-center gap-0 border-l border-r border-[var(--border)]">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `px-5 h-16 flex items-center whitespace-nowrap micro-label hover:text-[var(--accent)] transition-colors border-r last:border-r-0 border-[var(--border)] ${
                  isActive ? "text-[var(--accent)] bg-[var(--bg-2)]" : "text-[var(--fg)]"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 min-w-0">
          {user && user !== false ? (
            <>
              <NavLink
                to="/bulk"
                className="btn-ghost hidden lg:inline-flex items-center gap-2 whitespace-nowrap"
                data-testid="nav-bulk"
              >
                <Stack size={14} weight="bold" /> Bulk
              </NavLink>
              <NavLink
                to="/showcase"
                className="btn-ghost hidden sm:inline-flex items-center gap-2 whitespace-nowrap"
                data-testid="nav-showcase"
              >
                <Stack size={14} weight="bold" /> Showcase
              </NavLink>
              <NavLink
                to="/dashboard"
                className="btn-ghost hidden lg:inline-flex items-center gap-2 whitespace-nowrap"
                data-testid="nav-dashboard"
              >
                <Gauge size={14} weight="bold" /> Dashboard
              </NavLink>
              {user.role === "admin" && (
                <NavLink
                  to="/admin/billing"
                  className="btn-ghost hidden lg:inline-flex items-center gap-2 whitespace-nowrap text-[var(--accent)]"
                  data-testid="nav-admin-billing"
                >
                  Admin
                </NavLink>
              )}
              <div
                className="hidden md:flex items-center gap-2 px-3 h-10 border border-[var(--border)] max-w-[220px]"
                data-testid="user-chip"
              >
                <User size={14} weight="bold" className="shrink-0" />
                <span className="font-mono text-xs truncate">{user.email}</span>
              </div>
              <button onClick={onLogout} className="btn-ghost" data-testid="btn-logout">
                <SignOut size={14} weight="bold" />
              </button>
            </>
          ) : (
            <Link to="/login" className="btn-ghost" data-testid="btn-login-nav">
              Sign in
            </Link>
          )}
          <button
            className="md:hidden btn-ghost"
            onClick={() => setMobileOpen((o) => !o)}
            aria-label="toggle nav"
            data-testid="btn-mobile-menu"
          >
            ≡
          </button>
        </div>
      </div>
      {mobileOpen && (
        <div
          className="md:hidden border-t border-[var(--border)] bg-white"
          data-testid="mobile-nav"
        >
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `block px-6 py-4 micro-label border-b border-[var(--border)] ${
                  isActive ? "text-[var(--accent)]" : ""
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
          {user && user !== false && (
            <>
              <NavLink to="/bulk" onClick={() => setMobileOpen(false)} className="block px-6 py-4 micro-label border-b border-[var(--border)]">
                Bulk convert
              </NavLink>
              <NavLink to="/showcase" onClick={() => setMobileOpen(false)} className="block px-6 py-4 micro-label border-b border-[var(--border)]">
                Showcase
              </NavLink>
              <NavLink to="/dashboard" onClick={() => setMobileOpen(false)} className="block px-6 py-4 micro-label border-b border-[var(--border)]">
                Dashboard
              </NavLink>
              {user.role === "admin" && (
                <NavLink to="/admin/billing" onClick={() => setMobileOpen(false)} className="block px-6 py-4 micro-label border-b border-[var(--border)] text-[var(--accent)]">
                  Admin · Billing
                </NavLink>
              )}
            </>
          )}
        </div>
      )}
    </header>
  );
}
